import type { U2000FavoriteMenuItem } from "../types/U2000FavoriteMenu";

/** Tri stable par `order` (favoris menu). */
export function u2000SortFavoriteItemsByOrder(items: U2000FavoriteMenuItem[]): U2000FavoriteMenuItem[] {
  return [...items].sort((a, b) => a.order - b.order);
}

/**
 * Parse la valeur JSON localStorage des favoris.
 * Retourne [] si absent, invalide ou non-tableau.
 */
export function u2000ParseFavoriteItemsFromStorage(raw: string | null): U2000FavoriteMenuItem[] {
  if (!raw) return [];
  try {
    const items = JSON.parse(raw) as U2000FavoriteMenuItem[];
    return Array.isArray(items) ? items : [];
  } catch {
    return [];
  }
}

/**
 * Résout la liste de favoris à utiliser : state React → state localStorage hook → raw storage.
 * BL-0337 : un state vide ne doit pas masquer des favoris déjà écrits en localStorage.
 */
export function u2000ResolveFavoriteItemsList(
  stateItems: U2000FavoriteMenuItem[],
  localFavorites: U2000FavoriteMenuItem[],
  storageItems: U2000FavoriteMenuItem[]
): U2000FavoriteMenuItem[] {
  if (stateItems.length > 0) {
    return u2000SortFavoriteItemsByOrder(stateItems);
  }
  if (localFavorites.length > 0) {
    return u2000SortFavoriteItemsByOrder(localFavorites);
  }
  if (storageItems.length > 0) {
    return u2000SortFavoriteItemsByOrder(storageItems);
  }
  return [];
}

export function u2000FindFavoriteByPath(items: U2000FavoriteMenuItem[], path: string): U2000FavoriteMenuItem | undefined {
  return items.find((item) => item.path === path);
}

export interface U2000DefaultScreenDecision {
  /** Path à exposer comme écran par défaut (`null` = aucun). */
  path: string | null;
  /** `true` → effacer `defaultScreenPath` des settings. */
  shouldClear: boolean;
}

/**
 * Décide lecture / clear de `defaultScreenPath` (BL-0337).
 *
 * - Path présent dans la liste → renvoyer le path
 * - Path absent et liste **non vide** → clear (favori retiré)
 * - Path absent et liste **vide** → ne pas clear (state temporaire / avant resync storage)
 */
export function u2000ResolveDefaultScreenDecision(defaultPath: string | undefined | null, favoriteItems: U2000FavoriteMenuItem[]): U2000DefaultScreenDecision {
  if (!defaultPath) {
    return { path: null, shouldClear: false };
  }
  if (u2000FindFavoriteByPath(favoriteItems, defaultPath)) {
    return { path: defaultPath, shouldClear: false };
  }
  if (favoriteItems.length > 0) {
    return { path: null, shouldClear: true };
  }
  return { path: null, shouldClear: false };
}

/** `true` si le path peut être défini comme écran par défaut. */
export function u2000CanSetDefaultScreenPath(path: string, favoriteItems: U2000FavoriteMenuItem[]): boolean {
  return Boolean(u2000FindFavoriteByPath(favoriteItems, path));
}
